import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
  LineChart, Line, CartesianGrid,
} from "recharts";
import { PawPrint, MapPin, Sparkles, Users, TrendingUp, ArrowRight, Star } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "matched", "converted", "completed"];
const STATUS_COLORS = {
  new: "bg-primary/15 text-primary",
  contacted: "bg-accent/30 text-accent-foreground",
  matched: "bg-secondary text-secondary-foreground",
  converted: "bg-primary text-primary-foreground",
  completed: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300",
};
const CHART = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

export default function AdminDashboard() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const [s, l, tr] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/leads"),
      api.get("/trainers"),
    ]);
    setStats(s.data);
    setLeads(l.data);
    setTrainers(tr.data);
  };
  useEffect(() => { load(); }, []);

  const updateLead = async (id, body) => {
    try {
      await api.patch(`/leads/${id}`, body);
      toast.success("Lead updated");
      await load();
    } catch (e) {
      toast.error("Could not update");
    }
  };

  const filtered = leads.filter((l) => statusFilter === "all" || l.status === statusFilter);

  if (!stats) return <div className="p-10 text-muted-foreground">Loading…</div>;

  const countryData = [
    { name: "🇺🇸 US", value: stats.by_country.US || 0 },
    { name: "🇲🇽 MX", value: stats.by_country.MX || 0 },
  ];
  const serviceData = Object.entries(stats.by_service).map(([k, v]) => ({ name: k.replace("_", " "), value: v }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">{t("admin.title")}</h1>
          <p className="text-muted-foreground">CRM · Pipeline · Trainers · Coverage</p>
        </div>
        <Badge className="rounded-full bg-primary text-primary-foreground" data-testid="admin-badge">
          <PawPrint className="w-3.5 h-3.5 mr-1.5" /> Admin
        </Badge>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="rounded-full p-1 bg-muted">
          <TabsTrigger value="overview" className="rounded-full px-5" data-testid="admin-tab-overview">{t("admin.tab.overview")}</TabsTrigger>
          <TabsTrigger value="leads" className="rounded-full px-5" data-testid="admin-tab-leads">{t("admin.tab.leads")}</TabsTrigger>
          <TabsTrigger value="trainers" className="rounded-full px-5" data-testid="admin-tab-trainers">{t("admin.tab.trainers")}</TabsTrigger>
          <TabsTrigger value="coverage" className="rounded-full px-5" data-testid="admin-tab-coverage">{t("admin.tab.coverage")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6 space-y-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Leads", v: stats.totals.leads, icon: TrendingUp, color: "bg-primary/15 text-primary" },
              { label: "Trainers", v: stats.totals.trainers, icon: Users, color: "bg-accent/30 text-accent-foreground" },
              { label: "Parents", v: stats.totals.parents, icon: PawPrint, color: "bg-secondary/40 text-secondary-foreground" },
              { label: "Sessions", v: stats.totals.sessions, icon: Sparkles, color: "bg-destructive/15 text-destructive" },
            ].map((k, i) => (
              <Card key={i} className="rounded-3xl border-border/60 soft-shadow">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${k.color}`}>
                    <k.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-3xl font-black">{k.v}</p>
                    <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{k.label}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="rounded-3xl border-border/60 soft-shadow">
              <CardContent className="p-6">
                <h3 className="font-display font-bold mb-4">Conversion funnel</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={stats.funnel}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="stage" stroke="currentColor" fontSize={12} />
                    <YAxis stroke="currentColor" fontSize={12} />
                    <Tooltip contentStyle={{ borderRadius: 16, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                    <Bar dataKey="count" fill="hsl(var(--chart-1))" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="rounded-3xl border-border/60 soft-shadow">
              <CardContent className="p-6">
                <h3 className="font-display font-bold mb-4">U.S. vs México</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={countryData} dataKey="value" innerRadius={60} outerRadius={90} label>
                      {countryData.map((_, i) => <Cell key={i} fill={CHART[i]} />)}
                    </Pie>
                    <Legend />
                    <Tooltip contentStyle={{ borderRadius: 16, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="rounded-3xl border-border/60 soft-shadow lg:col-span-2">
              <CardContent className="p-6">
                <h3 className="font-display font-bold mb-4">Services demand</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={serviceData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis type="number" stroke="currentColor" fontSize={12} />
                    <YAxis type="category" dataKey="name" stroke="currentColor" fontSize={12} width={150} />
                    <Tooltip contentStyle={{ borderRadius: 16, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                    <Bar dataKey="value" fill="hsl(var(--chart-2))" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card className="rounded-3xl border-border/60 soft-shadow">
            <CardContent className="p-6">
              <h3 className="font-display font-bold mb-4">Recent leads</h3>
              <div className="space-y-2">
                {stats.recent_leads.map((l) => (
                  <div key={l.id} className="flex items-center justify-between p-3 rounded-2xl hover:bg-muted/40">
                    <div>
                      <p className="font-semibold">{l.parent_name} · {l.dog_name}</p>
                      <p className="text-xs text-muted-foreground">{l.city}, {l.country} · {l.service}</p>
                    </div>
                    <Badge className={`rounded-full ${STATUS_COLORS[l.status]}`}>{l.status}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leads" className="mt-6 space-y-4">
          <div className="flex items-center gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] rounded-full" data-testid="admin-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">{filtered.length} leads</span>
          </div>
          <Card className="rounded-3xl border-border/60 soft-shadow overflow-hidden">
            <div className="divide-y divide-border/60">
              {filtered.map((l) => (
                <button
                  key={l.id}
                  onClick={() => setSelected(l)}
                  className="w-full text-left p-4 hover:bg-muted/40 transition flex items-center justify-between gap-4"
                  data-testid={`admin-lead-${l.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-2xl bg-primary/15 text-primary flex items-center justify-center font-bold uppercase">
                      {l.parent_name?.[0] || "?"}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold truncate">{l.parent_name} · {l.dog_name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        <MapPin className="w-3 h-3 inline mr-1" />
                        {l.city}, {l.country} · {l.service}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={`rounded-full ${STATUS_COLORS[l.status]}`}>{l.status}</Badge>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="trainers" className="mt-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {trainers.map((tr) => (
              <Card key={tr.id} className="rounded-3xl border-border/60 soft-shadow lift">
                <CardContent className="p-5 flex gap-4">
                  <img src={tr.avatar} alt={tr.name} className="w-14 h-14 rounded-2xl object-cover" />
                  <div className="min-w-0">
                    <p className="font-bold truncate">{tr.name}</p>
                    <p className="text-xs text-muted-foreground">{tr.city}, {tr.country}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Star className="w-3.5 h-3.5 fill-accent text-accent" />
                      <span className="text-xs font-bold">{tr.rating}</span>
                      <span className="text-xs text-muted-foreground">· {tr.years_experience}y</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(tr.specialties || []).slice(0, 2).map((s) => (
                        <span key={s} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary font-bold">{s}</span>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="coverage" className="mt-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="rounded-3xl border-border/60 soft-shadow">
              <CardContent className="p-6">
                <h3 className="font-display font-bold mb-3 flex items-center gap-2">🇺🇸 United States</h3>
                <div className="flex flex-wrap gap-2">
                  {stats.coverage.US.map((c) => (
                    <span key={c} className="px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold">{c}</span>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-3xl border-border/60 soft-shadow">
              <CardContent className="p-6">
                <h3 className="font-display font-bold mb-3 flex items-center gap-2">🇲🇽 México</h3>
                <div className="flex flex-wrap gap-2">
                  {stats.coverage.MX.map((c) => (
                    <span key={c} className="px-3 py-1.5 rounded-full bg-accent/30 text-accent-foreground text-xs font-bold">{c}</span>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Lead detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="rounded-3xl max-w-2xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">
                  {selected.parent_name} · {selected.dog_name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={`rounded-full ${STATUS_COLORS[selected.status]}`}>{selected.status}</Badge>
                  <Badge variant="outline" className="rounded-full">{selected.country}</Badge>
                  <Badge variant="outline" className="rounded-full">{selected.city}</Badge>
                  <Badge variant="outline" className="rounded-full">{selected.service}</Badge>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div><span className="text-muted-foreground">Email:</span> {selected.parent_email}</div>
                  <div><span className="text-muted-foreground">Phone:</span> {selected.parent_phone}</div>
                  <div><span className="text-muted-foreground">Breed:</span> {selected.dog_breed}</div>
                  <div><span className="text-muted-foreground">Schedule:</span> {selected.preferred_schedule}</div>
                </div>
                {selected.goals && (
                  <p className="text-sm bg-muted/40 rounded-xl p-3"><span className="font-bold">Goals:</span> {selected.goals}</p>
                )}
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest mb-2">Update status</p>
                  <div className="flex flex-wrap gap-2">
                    {STATUSES.map((s) => (
                      <Button
                        key={s}
                        size="sm"
                        variant={selected.status === s ? "default" : "outline"}
                        className="rounded-full"
                        onClick={() => updateLead(selected.id, { status: s })}
                        data-testid={`admin-set-status-${s}`}
                      >
                        {s}
                      </Button>
                    ))}
                  </div>
                </div>
                {selected.suggested_trainer_ids?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest mb-2">Assign trainer</p>
                    <div className="flex flex-wrap gap-2">
                      {trainers
                        .filter((tr) => selected.suggested_trainer_ids.includes(tr.id))
                        .map((tr) => (
                          <Button
                            key={tr.id}
                            size="sm"
                            variant={selected.assigned_trainer_id === tr.id ? "default" : "outline"}
                            className="rounded-full"
                            onClick={() => updateLead(selected.id, { assigned_trainer_id: tr.id, status: "matched" })}
                            data-testid={`admin-assign-${tr.id}`}
                          >
                            {tr.name}
                          </Button>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
